// Product analytics — TWO complementary providers, one wrapper.
//
// This is a Firebase app, so we run BOTH, because they do different jobs:
//   • PostHog  — in-PRODUCT behavior: autocapture (every click/pageview with no
//     per-element wiring), session replay + heatmaps (where users stall),
//     funnels + retention + cohorts, feature flags. This is "where they click
//     and where they get stuck."
//   • GA4 / Google Analytics for Firebase — the Google-native ACQUISITION layer:
//     Firebase console dashboards, free unlimited events, raw BigQuery export
//     (no sampling — huge for a data team), Google Ads attribution, and
//     audiences. This is "where traffic comes from and campaign ROI."
// Neither alone covers both; the best-in-class setup is both, firing the SAME
// custom events through one `track()`.
//
// SAFE BY DEFAULT: each provider is independently no-op until its key exists —
// PostHog on VITE_POSTHOG_KEY, GA4 on the Firebase measurementId. Ships dark;
// each "goes live" the moment its key is present, no code change, no events in
// dev/CI/tests. Privacy: autocapture sends no element text and no attribute
// values. Session replay, heatmaps, dead clicks and web vitals are ON for
// every visitor (#4475, founder 2026-10-16, reversing #2511's lockdown):
// replay shows the visible page with every input masked, Astra conversations
// blocked and crisis panels blocked. Copied text, exceptions, console logs and
// surveys stay off. The rules live in ./analyticsPrivacy.js. Essays,
// transcripts and chat render as ordinary divs, so input-only masking would
// leak them: they carry their own block/mask classes. We identify by a salted pseudonymous id
// (utils/analyticsId, fix 4 - never the raw email) and NEVER send
// transcript/essay/chat text as event props.

import { carriesCrisis, posthogPrivacyConfig, safeUrl } from './analyticsPrivacy';
import { isTeamDevice } from '../utils/teamDevice';

let ph = null;              // loaded posthog instance, or null when disabled
let phEnabled = false;
let ga = null;              // firebase analytics instance, or null when disabled
let gaLog = null;           // firebase/analytics logEvent, bound lazily
let gaSetUserId = null;
let gaSetUserProps = null;
let gaSetDefaults = null;   // firebase/analytics setDefaultEventParameters
let gaEnabled = false;

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const GA_ID = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;


async function initPostHog() {
    if (phEnabled || !KEY) return;
    // Do Not Track / Global Privacy Control: PostHog is never loaded, so no
    // request at all goes out (respect_dnt only drops events; security
    // review 2026-09-29, #2511). The same gate as GA4 below.
    if (dntEnabled()) return;
    const mod = await import('posthog-js');
    ph = mod.default || mod.posthog || mod;
    ph.init(KEY, {
        api_host: HOST,
        person_profiles: 'identified_only',   // don't create profiles for anon noise
        capture_pageview: false,               // we fire SPA pageviews on route change
        capture_pageleave: true,               // engagement/time-on-page needs leave events
        // Every privacy key, at the level posthog-js reads it. They once sat
        // inside `autocapture: {}`, where the library never looks, and clicked
        // text shipped for three months (#2511). Do not inline them here: the
        // shape test pins analyticsPrivacy.js, and a key moved back into a
        // nested object is silently ignored by the library.
        ...posthogPrivacyConfig(),
        respect_dnt: true,
    });
    // Legacy-cookie migration (founder-caught 2026-08-23). Devices identified
    // BEFORE the hashed-identity flip (2026-08-17) persist the RAW EMAIL as
    // their PostHog distinct_id. A signed-in session eventually re-identifies
    // to the u_ hash, but a signed-out or bounced-early return visit never
    // does — every pageview/pageleave/autocapture kept emitting under the
    // email (live rows in Explore sessions dated days after the flip).
    // Rebind such a cookie to its hashed id right here at init: same person,
    // no email on the wire, before any queued event replays. An INTERNAL
    // email opts out entirely — its cookie'd pageviews were re-polluting
    // funnels through the same gap.
    try {
        const cur = typeof ph.get_distinct_id === 'function' ? ph.get_distinct_id() : '';
        if (cur && String(cur).includes('@')) {
            if (isInternalEmail(cur)) {
                ph.opt_out_capturing?.();
                ph.reset();
            } else {
                const { analyticsId } = await import('../utils/analyticsId');
                const id = await analyticsId(cur);
                // Cannot hash -> never keep emitting as the email: drop to a
                // fresh anonymous id instead (a person-split beats a leak).
                if (id) ph.identify(id); else ph.reset();
            }
        }
    } catch { /* never throw */ }
    phEnabled = true;
}

// Do Not Track: PostHog honors it natively (respect_dnt above); GA4 has no
// such option, so we gate its init ourselves. Without this, the privacy
// policy's "we honor your browser's Do Not Track setting" would be true for
// only one of the two named providers (reviewer-caught, compliance fix 1).
// Global Privacy Control is the same request; the server honors both
// (lab_events.browser_opted_out), so the browser does too.
function dntEnabled() {
    try {
        return navigator.doNotTrack === '1' || window.doNotTrack === '1'
            || navigator.msDoNotTrack === '1' || navigator.globalPrivacyControl === true;
    } catch { return false; }
}

// GA4 reports this title on every hit instead of document.title, which on a
// school or essay page names what the student is reading (#2511).
const GA4_PAGE_TITLE = 'Stratia Admissions';

async function initGA4() {
    if (gaEnabled || !GA_ID) return;
    if (dntEnabled()) return;
    // isSupported guards SSR / unsupported browsers (analytics uses cookies + IDB).
    const [{ initializeAnalytics, logEvent, setUserId, setUserProperties,
        setDefaultEventParameters, isSupported },
        { default: app }] = await Promise.all([
        import('firebase/analytics'),
        import('../firebase'),
    ]);
    if (!(await isSupported())) return;
    // No automatic page_view, and every hit carries the scrubbed URL and a
    // fixed title (security review 2026-09-29, #2511): getAnalytics() sent
    // its own page_view at startup, and every GA4 hit (session_start,
    // user_engagement, the conversions) carried the raw location (?q=,
    // ?ask=, /feedback?e=<email>) and document.title. pageview() re-sets
    // both on every route change. Reads measurementId from the app config.
    ga = initializeAnalytics(app, { config: {
        send_page_view: false, page_location: reportableHref(), page_title: GA4_PAGE_TITLE,
        // session_start and user_engagement fire before any pageview(): a team
        // device's first hits carry the internal marker too (#3021)
        ...(isOurBrowser() ? GA4_TEAM_PARAMS : {}),
    } });
    gaLog = logEvent;
    gaSetDefaults = setDefaultEventParameters;
    gaSetUserId = setUserId;
    gaSetUserProps = setUserProperties;
    gaEnabled = true;
}

// Calls made BEFORE the async init resolves are queued and replayed in order
// once the providers exist. Without this, every session's first pageview, any
// mount-time track (onboarding_started), and the mobile redirect-return
// sign-in events (signed_up!) silently vanished — the dynamic import always
// loses the race against synchronous effects. Bounded so a broken init can't
// grow memory forever.
let initFinished = false;
let initPromise = null;
const pending = [];

function defer(replay) {
    if (pending.length < 200) pending.push(replay);
}

/**
 * Initialize both providers once, as early as possible. No-ops per provider
 * without its key. Called from App on mount. Idempotent (StrictMode-safe),
 * and flushes any events queued while the providers were still loading.
 */
export function initAnalytics() {
    if (!initPromise) {
        initPromise = Promise.allSettled([
            initPostHog().catch((e) => console.warn('[analytics] posthog init failed:', e?.message)),
            initGA4().catch((e) => console.warn('[analytics] ga4 init failed:', e?.message)),
        ]).then(() => {
            initFinished = true;
            applyTeamDevice();
            for (const replay of pending.splice(0)) {
                try { replay(); } catch { /* never throw */ }
            }
        });
    }
    return initPromise;
}

// Team devices (#3021): a browser marked by the team link (utils/teamDevice)
// keeps capturing — the founder's rule is that our own traffic is excluded at
// ANALYSIS time — but every event carries `internal: true` so each read can
// drop it (analytics_identity.team_traffic_sql). PostHog: a super property
// (rides every event, anonymous ones included) plus the person property.
// GA4: the `traffic_type=internal` event parameter its Internal Traffic data
// filter matches. A parameter, never a GA4 user property: the policy's "GA4
// receives only page views and the two conversions" stays literally true.
// Re-applied after init and after every reset(), which clears super
// properties.
export const TEAM_PROPS = { internal: true };
export const GA4_TEAM_PARAMS = { traffic_type: 'internal' };

// A browser driven by automation (Playwright, the recorders, the runner's
// Chrome) says so in navigator.webdriver; it is never a visitor, marked or
// not (#3807: the Oct 10 tutorial takes counted as visitors).
export function isAutomatedBrowser() {
    try { return typeof navigator !== 'undefined' && navigator.webdriver === true; } catch { return false; }
}
export const isOurBrowser = () => isTeamDevice() || isAutomatedBrowser();

export function applyTeamDevice() {
    if (!isOurBrowser()) return false;
    if (phEnabled && ph) {
        try { ph.register(TEAM_PROPS); ph.setPersonProperties(TEAM_PROPS); } catch { /* never throw */ }
    }
    if (gaEnabled && ga) { try { gaSetDefaults?.(GA4_TEAM_PARAMS); } catch { /* never throw */ } }
    return true;
}

/** Undo applyTeamDevice for this browser (the page's "Remove the marker"):
 * PostHog keeps super properties in its own storage, so clearing our marker
 * alone left internal: true on every later event (review of #3021). */
export function clearTeamMarker() {
    if (phEnabled && ph) {
        try { ph.unregister?.('internal'); ph.setPersonProperties({ internal: false }); } catch { /* never throw */ }
    }
    if (gaEnabled && ga) { try { gaSetDefaults?.({ traffic_type: null }); } catch { /* never throw */ } }
}

// Internal accounts (audit H8, 2026-08-09). Their sessions were landing in
// the SAME PostHog project and GA4 property as real students — inflating
// every funnel in the console, and internal purchase/sign_up events plausibly
// reached Google Ads bidding. The daily digest splits internal/external
// server-side, but no console view did. Once identify() sees an internal
// email, every subsequent track()/pageview is dropped client-side.
const INTERNAL_EMAILS = new Set([
    'cvsubs@gmail.com', 'stratiaadmissions@gmail.com', 'stratiauser@gmail.com',
    'stratiauser1@gmail.com', 'stratiauser2@gmail.com', 'kaaimd@gmail.com',
    'duser8531@gmail.com',
    'cvsubs101@gmail.com', 'cvsubs102@gmail.com', 'cvsubs@stratiaadmissions.com',
    // Household accounts (founder, 2026-10-17, #4600).
    'aditisubramanian1@gmail.com', 'srgyts@gmail.com', 'kirtisubs@gmail.com',
]);
// The set above is the customer book's INTERNAL (marketing_agent/customers.py),
// pinned equal by tests/cloud_functions/test_internal_set_twins.py (#4384).
function isInternalEmail(email) {
    // Plus-aliases of the ops inbox (stratiaadmissions+counselor@, the staged
    // desk students stratiaadmissions+console-NN@) are fixtures too, as is
    // every stratiauser<N>@ test account.
    const e = String(email || '').trim().toLowerCase();
    return INTERNAL_EMAILS.has(e) || e.startsWith('stratiaadmissions+')
        || /^stratiauser\d*@gmail\.com$/.test(e);
}
let internalSession = false;
export const isInternalSession = () => internalSession;

// Identity (compliance fix 4, 2026-08-16; everyone since 2026-08-17): the
// salted pseudonymous id (u_<hex>), never the raw email. GA4's terms prohibit
// PII in the user-id.
let identifySeq = 0;   // stale async hash must never land after reset/re-identify

async function identifyAs(email, traits, seq) {
    const { analyticsId } = await import('../utils/analyticsId');
    const id = await analyticsId(email);
    // NEVER falls back to the raw email: an environment that cannot hash
    // sends no identity at all.
    if (!id) return;
    if (seq !== identifySeq) return;   // superseded while hashing
    if (phEnabled && ph) { try { ph.opt_in_capturing?.(); ph.identify(id, traits); } catch { /* never throw */ } }
    if (gaEnabled && ga) { try { gaSetUserId(ga, id); } catch { /* never throw */ } }
}

/** Bind events to the signed-in user (call on auth). */
export function identify(email, traits = {}) {
    if (!email) return;
    internalSession = isInternalEmail(email);
    if (internalSession) {
        try { ph?.opt_out_capturing?.(); } catch { /* never throw */ }
        return;                       // never identified, never captured
    }
    if (!initFinished) { defer(() => identify(email, traits)); return; }
    identifyAs(email, traits, ++identifySeq);
}

/** Clear identity on logout so the next user isn't merged into this one. */
export function resetIdentity() {
    internalSession = false;
    identifySeq += 1;                 // invalidate any in-flight hash
    if (!initFinished) { defer(() => resetIdentity()); return; }
    if (phEnabled && ph) { try { ph.reset(); } catch { /* never throw */ } }
    if (gaEnabled && ga) { try { gaSetUserId(ga, null); } catch { /* never throw */ } }
    applyTeamDevice();
}

/** The URL as analytics may report it, through safeUrl (analyticsPrivacy.js):
 * NEVER the fragment (/review magic links carry a bearer SECRET there, PR #854
 * finding; belt-and-braces with the review page's own module-load scrub), and
 * only campaign parameters in the query. grant/token are redeemable secrets
 * from emailed links, e is a raw email (security review 2026-07-27, #4), q/ask
 * are words the student typed (#2511); the allowlist drops all of them and
 * whatever parameter is added next. */
export function reportableHref() {
    return safeUrl(window.location.href);
}

// GA4 treats these event-parameter names as TRAFFIC SOURCE, not as dimensions:
// sending `source: 'add_college'` on any event re-attributes that whole session
// away from however the user actually arrived.
//
// We were doing exactly that. On 2026-08-04 the GA4 Session source/medium report
// listed `add_college`, `add_college_analysis`, `financial_aid_office` and
// `profile` as traffic sources — our own UI-origin labels — and ~50% of sessions
// landed in "Unassigned". Paid sessions were being overwritten the moment the
// visitor clicked a source link or added a college, so paid was UNDER-counted.
//
// Renaming happens here, at the single choke point, rather than at ~20 call
// sites: any future `source:` prop is neutralized automatically. PostHog keeps
// the original key — it has no reserved names and its history stays comparable.
const GA4_RESERVED_PARAMS = [
    'source', 'medium', 'campaign', 'term', 'content', 'campaign_id',
    'gclid', 'dclid', 'srsltid', 'source_platform', 'creative_format',
    'marketing_tactic',
];

export function ga4SafeProps(props = {}) {
    if (!props || typeof props !== 'object') return props;
    let out = null;
    for (const key of GA4_RESERVED_PARAMS) {
        if (Object.prototype.hasOwnProperty.call(props, key)) {
            out = out || { ...props };
            // Never clobber an explicit ui_* the caller already set.
            if (!Object.prototype.hasOwnProperty.call(out, `ui_${key}`)) {
                out[`ui_${key}`] = out[key];
            }
            delete out[key];
        }
    }
    return out || props;
}

/** SPA pageview — call on every route change. `props` may carry the path. */
export function pageview(path, props = {}) {
    if (internalSession) return;
    // Snapshot the URL now — by flush time the SPA may have navigated on.
    const href = reportableHref();
    if (!initFinished) { defer(() => pageview(path, { $current_url: href, ...props })); return; }
    if (phEnabled && ph) {
        try { ph.capture('$pageview', { $current_url: href, path, ...props }); } catch { /* never throw */ }
    }
    if (gaEnabled && ga) {
        try {
            gaSetDefaults?.({ page_location: href, page_title: GA4_PAGE_TITLE,
                ...(isOurBrowser() ? GA4_TEAM_PARAMS : {}) });
            gaLog(ga, 'page_view', ga4SafeProps({
                page_path: path, page_location: href, page_title: GA4_PAGE_TITLE, ...props,
            }));
        } catch { /* never throw */ }
    }
}

/**
 * A custom conversion event, fired to BOTH providers. `name` should come from
 * EVENTS below so the taxonomy stays consistent (see docs/analytics.md). Props
 * are dimensions — never document/essay/chat text.
 */
// GA4's event diet (compliance fix 5, 2026-08-17): Google receives ONLY the
// two Ads conversions — 'purchase' and 'sign_up', the exact pair imported
// into Google Ads (marketing/google-ads-setup.md §1.5: "Two conversions,
// ever") — plus page_view from pageview() below, which GA4's
// channel/session reporting (the digest's acquisition section) needs.
// Every product-taxonomy event (colleges added, essays, roadmap, chat...)
// is covered information in SOPIPA's sense and stays PostHog-only. This
// changes NOTHING for Ads bidding (it only ever imported these two) and
// nothing for the digest (its GA4 reads are channel dimensions).
const GA4_FORWARDED_EVENTS = new Set(['purchase', 'sign_up']);

// The crisis/distress check (#4294) lives beside the before_send scrub;
// re-exported so callers and tests reach it from the one analytics module.
export { carriesCrisis };

export function track(name, props = {}) {
    if (internalSession) return;   // internal accounts never reach the funnels
    if (!name) return;
    if (carriesCrisis(name, props)) return;   // fail closed, both providers (#4294)
    if (!initFinished) { defer(() => track(name, props)); return; }
    if (phEnabled && ph) { try { ph.capture(name, props); } catch { /* never throw */ } }
    // GA4 event names must be snake_case, <=40 chars, [a-z0-9_] — EVENTS comply.
    if (gaEnabled && ga && GA4_FORWARDED_EVENTS.has(name)) { try { gaLog(ga, name, ga4SafeProps(props)); } catch { /* never throw */ } }
}

/**
 * A completed payment, fired as BOTH our taxonomy event (credits_purchased /
 * subscription_started) AND GA4's standard `purchase` (value + currency +
 * transaction_id) — the standard name is what Google Ads imports for
 * value-based conversion bidding, the taxonomy name is what our funnels use.
 * Caller dedupes (a refreshed success page must not double-count revenue).
 */
export function trackPurchase({ productId, value, transactionId, isSubscription, currency = 'USD' }) {
    // Reference the taxonomy constants, not string literals: a 2026-07 analytics
    // audit grepped for EVENTS.CREDITS_PURCHASED, found nothing, and concluded
    // the revenue funnel was dead when it had been firing all along.
    // `currency`: the rupee Season Pass (#4966) reports its value in INR, and
    // GA4 converts; never a dollar figure for a rupee charge.
    const taxonomyEvent = isSubscription ? EVENTS.SUBSCRIPTION_STARTED : EVENTS.CREDITS_PURCHASED;
    track(taxonomyEvent, { product_id: productId, value, currency });
    track(EVENTS.GA4_PURCHASE, {
        transaction_id: transactionId,
        value,
        currency,
        items: [{ item_id: productId, item_name: productId, price: value, quantity: 1 }],
    });
}

/**
 * Dimensions attached to every SUBSEQUENT event in this browser session.
 *
 * Distinct from setUserProps for two reasons that both bite on the landing
 * page, which is where this was introduced (2026-08-15):
 *   - person properties are dropped for anonymous visitors
 *     (`person_profiles: 'identified_only'` above), and a landing-page A/B
 *     read is almost entirely anonymous;
 *   - the route's $pageview fires before an async flag read can resolve, so
 *     the dimension has to ride the events that come AFTER it — the try-hero
 *     reads, the sign_up, the purchase. Those are the funnel questions.
 *
 * Register dimensions only. Anything that is an ACTION belongs in EVENTS.
 */
// Session/person properties are PostHog-ONLY since fix 5 (2026-08-17,
// reviewer-caught): the GA4 leg was sending user properties — including
// signup_domain, which for a school-domain account IS the school — and
// the policy's "Google Analytics receives only page views and
// purchase/sign-up confirmations" must be literally true. No GA4 consumer
// reads user properties (ga4_acquisition = channel dimensions; Ads = the
// two conversions), so nothing is lost. The diet test pins this.
export function registerSessionProps(props = {}) {
    if (internalSession) return;
    if (!initFinished) { defer(() => registerSessionProps(props)); return; }
    if (phEnabled && ph) { try { ph.register(props); } catch { /* never throw */ } }
}

/** Set durable person properties (grade, plan, cohort) for segmentation. */
export function setUserProps(props = {}) {
    if (internalSession) return;
    if (!initFinished) { defer(() => setUserProps(props)); return; }
    if (phEnabled && ph) { try { ph.setPersonProperties(props); } catch { /* never throw */ } }
}

/**
 * Person properties written ONCE and never overwritten ($set_once): the
 * first-touch acquisition facts (#2997). A later visit from a different
 * campaign must not rewrite how this person first found us. PostHog only;
 * internal sessions never reach it, the same rule as track().
 */
export function setUserPropsOnce(props = {}) {
    if (internalSession) return;
    if (!props || !Object.keys(props).length) return;
    if (!initFinished) { defer(() => setUserPropsOnce(props)); return; }
    if (phEnabled && ph) { try { ph.setPersonProperties(undefined, props); } catch { /* never throw */ } }
}

// ---------------------------------------------------------------------------
// Event taxonomy — the single source of truth for custom event names.
// object_verb, past tense. Autocapture covers raw clicks/pageviews; these are
// the CONVERSION MILESTONES that define funnels and activation. Keep in sync
// with docs/analytics.md.
export const EVENTS = {
    // ===== Astra concierge (audit H3, 2026-08-09 — the primary surface was
    // 100% dark; the ADR-0004 card confirm rate was unmeasured).
    CONCIERGE_OPENED: 'concierge_opened',
    // A Major Map "question to explore" tapped through to Astra (2026-08-09).
    // Worth its own event: it measures whether turning dead prose into a door
    // actually gets used, which is the whole premise of the change.
    MAJOR_MAP_QUESTION_ASKED: 'major_map_question_asked',
    CONCIERGE_MESSAGE_SENT: 'concierge_message_sent',
    // #2033: the client gave up on a turn the backend was still answering.
    // Feeds the digest's concierge section; props are route + how long we waited.
    CONCIERGE_TIMEOUT: 'concierge_timeout',
    CONCIERGE_ACTION_CONFIRMED: 'concierge_action_confirmed',
    CONCIERGE_ACTION_DECLINED: 'concierge_action_declined',
    // #2938: an Astra add, as the student saw it. LISTED = the confirm came
    // back with the schools on the list (ms from the click); ANALYSED = every
    // school's fit + major-chances run settled (ms from the click), fired by
    // the chat's status line (#2942). The
    // server emits astra_colleges_added / astra_colleges_fit_ready for the
    // same batch, so a student who leaves mid-run is still counted.
    ASTRA_ADD_LISTED: 'astra_add_listed',
    ASTRA_ADD_ANALYSED: 'astra_add_analysed',
    // #2942: the Launchpad showed a card's fit analysis still running (once
    // per batch per page load), i.e. the student came to watch it there.
    LAUNCHPAD_FIT_ANALYZING_SEEN: 'launchpad_fit_analyzing_seen',
    CONCIERGE_FEEDBACK_RATED: 'concierge_feedback_rated',
    // ===== Adopted rogue strings (audit C) — wire names unchanged so no
    // funnel discontinuity; they simply stop being unpinned literals.
    RESEARCH_ACTION_CLICKED: 'research_action_clicked',
    AGENT_CONNECTED_DETECTED: 'agent_connected_detected',
    AGENT_SETUP_LINK_CLICKED: 'agent_setup_link_clicked',
    PWA_INSTALL_PROMPTED: 'pwa_install_prompted',
    PWA_INSTALL_DISMISSED: 'pwa_install_dismissed',
    // Patent marking page (#2155): did anyone read it.
    PATENTS_VIEWED: 'patents_viewed',
    // GA4 standard names, fired deliberately alongside our taxonomy events
    // (Ads value bidding); constants so their intent is legible.
    GA4_PURCHASE: 'purchase',
    GA4_SIGN_UP: 'sign_up',
    // App health. A stale build is a tab that predates a deploy: its module
    // graph points at chunk hashes the CDN no longer serves. We auto-reload
    // once; these two tell us how often that happens and whether it worked.
    STALE_BUILD_RELOADED: 'stale_build_reloaded',   // {chunk}
    APP_ERROR_BOUNDARY: 'app_error_boundary',       // {stale_build, message, path}

    // Collaborative Review (#848, design §9) — the growth-loop metrics:
    // shares per active student and reviewer-side engagement.
    SHARE_CREATED: 'share_created',            // {sections, expires_days, require_otp, emailed}
    SHARE_REVOKED: 'share_revoked',
    REVIEW_OPENED: 'review_opened',            // {sections, needs_otp, first}
    REVIEW_SECTION_VIEWED: 'review_section_viewed', // {section}
    REVIEW_ENDED_VISIT: 'review_ended_visit',  // expired/revoked link opened
    // Comments on a shared link (#4987, ADR 0062): do students open the notes
    // their reviewers leave? Posting, replying and resolving are counted
    // server-side (share_comment_posted / _replied / _resolved), never here.
    SHARE_NOTES_OPENED: 'share_notes_opened',        // {from: launchpad|roadmap|share, open}
    // Counselor caseload (#1991) — the counselor-side funnel: do counselors
    // who request a link reach their caseload, and does the caseload lead
    // to plans actually opened / students actually nudged?
    COUNSELOR_DOOR_CLICKED: 'counselor_door_clicked',       // {source: 'header'|'landing_line'|'for_schools'}
    COUNSELOR_SIGNIN_VIEWED: 'counselor_signin_viewed',     // {} the signed-out sign-in rendered (#2682)
    COUNSELOR_LINK_REQUESTED: 'counselor_link_requested',   // {resend: bool}
    COUNSELOR_CASELOAD_OPENED: 'counselor_caseload_opened', // {total, waiting}
    COUNSELOR_PLAN_OPENED: 'counselor_plan_opened',         // {group}
    COUNSELOR_REMINDER_SENT: 'counselor_reminder_sent',     // {group}
    // Counselor console (#2287): the Marketplace doorway and the desk. Card
    // counts, booleans and enums only; never a student identifier.
    COUNSELOR_GOOGLE_SIGNIN: 'counselor_google_signin',     // {workspace_domain: bool}
    COUNSELOR_INVITE_COPIED: 'counselor_invite_copied',     // {}
    COUNSELOR_CONSOLE_VIEWED: 'counselor_console_viewed',   // {door_closing_14d, no_safety, no_essay_started, silent_14d, on_track, total}
    COUNSELOR_CARD_FILTERED: 'counselor_card_filtered',     // {card}
    COUNSELOR_STUDENT_OPENED: 'counselor_student_opened',   // {cards: n}
    COUNSELOR_RADAR_VIEWED: 'counselor_radar_viewed',       // {rows}
    COUNSELOR_NOTE_SAVED: 'counselor_note_saved',           // {}
    // Action Desk (#2408, admin console): does the founder clear the desk,
    // and through which door (click, key, batch)? The executed outcome is
    // emitted server-side (desk_action_executed) because it completes there.
    // Lanes + keys (ADR 0018 "A with keys"): does the founder work by
    // keyboard, batch, and filter by function? The batch's outcome per card
    // is server-side (desk_batch_approved, desk_action_executed).
    DESK_VIEWED: 'desk_viewed',                             // {open, done}
    DESK_BATCH_APPROVED: 'desk_batch_approved',             // {count, excluded}
    DESK_FUNCTION_FILTERED: 'desk_function_filtered',       // {category}
    DESK_SHORTCUT_USED: 'desk_shortcut_used',               // {key}
    DESK_ACTION_CLICKED: 'desk_action_clicked',             // {kind, action_id}
    DESK_STORY_LINK_COPIED: 'desk_story_link_copied',       // {slug} (#5267: does he use a Short's Story link)
    DESK_CARD_DISMISSED: 'desk_card_dismissed',             // {kind, reason}
    DESK_SEARCH_VIEWED: 'desk_search_viewed',               // {view} (#2565; since #4565 a status chip on Search & SEO > Overview, opening that view's sub-tab)
    DESK_SEARCH_TREND_OPENED: 'desk_search_trend_opened',   // {table, scope} (#3326, a row's trend chart)
    DESK_SEARCH_REINDEX_MARKED: 'desk_search_reindex_marked', // {} (#3657, "I requested indexing" on a re-index row)
    DESK_YOUTUBE_VIEWED: 'desk_youtube_viewed',             // {state, floor_met} (#3544: does he read the YouTube Studio block on Growth, and was it read)
    DESK_EARNINGS_VIEWED: 'desk_earnings_viewed',           // {state, programs_met} (#5117: does he read Growth > Earnings, and was it read)
    DESK_REACH_VIEWED: 'desk_reach_viewed',                 // {sources_read} (#4742: does he read the reach map on Growth, and how many sources read)
    DESK_REACH_LAYER_SWITCHED: 'desk_reach_layer_switched', // {layer, scope} (#4742: which source or map he looks at)
    DESK_YOUTUBE_EXPANDED: 'desk_youtube_expanded',         // {list: shorts|terms} (#3544: does he open the full per-Short or search-term list)
    DESK_ADS_VIEWED: 'desk_ads_viewed',                     // {state, launched} (#4088: does he read the Ads view on Growth, and was it read)
    DESK_ADS_EXPANDED: 'desk_ads_expanded',                 // {list: schools} (#4088: does he open the full per-school list)
    DESK_BRIEF_VIEWED: 'desk_brief_viewed',                 // {tab, state} (#3470: does he read the executive summary, and was it there)
    DESK_KB_VIEWED: 'desk_kb_viewed',                       // {with_errors} (#3057, Product, KB accuracy)
    DESK_KB_LEDGER_VIEWED: 'desk_kb_ledger_viewed',         // {state} (#4226: does he read the KB ledger, and was it counted)
    DESK_KB_LEDGER_DRILLED: 'desk_kb_ledger_drilled',       // {level: school|figure, family} (#4226: does he open a school or a figure's history)
    DESK_KB_LEDGER_FILTERED: 'desk_kb_ledger_filtered',     // {section, status, verified, sort, searched, matching} (#4924: which slices of the ledger he reads)
    DESK_KB_LEDGER_PICKED: 'desk_kb_ledger_picked',         // {how: dot|row|all|figure, kind: recheck|source|mixed, section, picked} (#4924: how he picks what to refresh)
    DESK_KB_LEDGER_RECHECK_QUEUED: 'desk_kb_ledger_recheck_queued', // {sections, singles, figures, schools, anyway} (#4924: does he re-check on demand, and how much)
    DESK_KB_LEDGER_SOURCE_SENT: 'desk_kb_ledger_source_sent', // {sections, singles, figures, schools} (#4924: does he send schools to the source batch)
    DESK_SESSIONS_VIEWED: 'desk_sessions_viewed',           // {state, digest} (#4477: does he read Growth > Sessions, and was a digest there)
    DESK_SESSION_OPENED: 'desk_session_opened',             // {from: list|digest|journey} (#4477: which way he reaches one session's record)
    DESK_SESSIONS_FILTERED: 'desk_sessions_filtered',       // {filter: source|device|outcome|tag, cleared} (#4477: which slices he reads)
    DESK_AUDIENCE_VIEWED: 'desk_audience_viewed',           // {window} (#2600, Growth, Audience)
    DESK_STEER_SENT: 'desk_steer_sent',                     // {kind, handed_off, images} (#2571, #5344; the note itself never)
    DESK_REQUEST_BOX_SENT: 'desk_request_box_sent',         // {tab: today|requests, outcome: sent|refused, images: n (#5178)} (#5155: does he use Tell the Desk, and from which door; the request's words never; server-side desk_request_created counts what landed)
    DESK_REQUESTS_VIEWED: 'desk_requests_viewed',           // {filter: open|waiting|done|all} (#5155: does he read his requests, and which slice)
    DESK_REQUEST_IMAGE_ADDED: 'desk_request_image_added',   // {via: paste|drop|button, outcome: added|wrong_type|too_big|too_many|failed, tab: today|requests|card} (#5178, card: Request a change #5344: does he attach screenshots, which way in, and how often one fails; never the file or its name)
    DESK_SPEC_ANSWER_SAVED: 'desk_spec_answer_saved',       // {edit, desk_default, outcome: saved|accepted_all|refused, accepted?} (#5027, #5121: desk_default = the Desk's recommended answer saved as it was; does he answer a proposal's open questions on the card; the stored answer is server-side desk_spec_question_answered; the words never)
    DESK_LAUNCH_PIECES_SET: 'desk_launch_pieces_set',       // {card_kind: feature_proposal|feature_launch, pieces, outcome: saved|refused} (#5129: does he change or keep the Desk's Short / email / tutorial pick; the stored choice is server-side desk_launch_pieces_set)
    DESK_RERENDER_REQUESTED: 'desk_rerender_requested',     // {kind: short|tutorial, where: today|calendar, card_status, has_note, outcome: queued|unchanged|refused_near_slot|refused|cancelled} (#3533; the note itself never)
    DESK_CLAUDE_TASK_CLICKED: 'desk_claude_task_clicked',   // {op: ask|send|cancel, mode: email|message|task, has_note, outcome: queued|unchanged|refused} (#3656; never the note or the words)
    DESK_LEARNING_CHANGED: 'desk_learning_changed',         // {op, edited} (#2571)
    DESK_CARD_FILTER_SET: 'desk_card_filter_set',           // {mode} (#2637)
    DESK_MOVE_JUMPED: 'desk_move_jumped',                   // {count} (#3110: does he look at the Desk's own work)
    DESK_CARD_RESTORED: 'desk_card_restored',               // {kind, noted} (#2637; never the note)
    DESK_VIEW_SWITCHED: 'desk_view_switched',               // {view, via} (#3152/#3156: Today | Brief | Calendar; does he use them)
    DESK_CALENDAR_POST_OPENED: 'desk_calendar_post_opened', // {state, kind, untracked, from} (#3152: which posts he inspects)
    DESK_CALENDAR_ACTED: 'desk_calendar_acted',             // {action_id} (#3152: does he act from the calendar, not only the lanes)
    DESK_CARD_RESCHEDULED: 'desk_card_rescheduled',         // {kind, status, outcome: moved|partial|refused} (#3196: does Change time get used, and does it land)
    DESK_CARD_DETAILS_OPENED: 'desk_card_details_opened',   // {kind, media: video|image|text|none} (2026-10-05: post cards fold captions and evidence behind Details; does he ever open it)
    DESK_COMMENTS_FILTERED: 'desk_comments_filtered',     // {control: state|platform|post|range} (#3804: does he use the Comments tab's chips and filters)
    DESK_COMMENT_OPENED: 'desk_comment_opened',           // {platform, state, via: click|key} (#3804: which comments he opens in the reading pane)
    DESK_COMMENT_ACTED: 'desk_comment_acted',             // {op, platform, words: draft|own|none, outcome: sent|queued|copied|done|undone|refused} (#3804: Send/Skip/Hide/Delete/Edit from the tab, and Undo; #4324/#4373: copied = TikTok's or LinkedIn's Copy and open; server events carry what landed)
    DESK_TIKTOK_VIEWED: 'desk_tiktok_viewed',               // {state, count} (#4649: does he open the TikTok tab, and how many Shorts wait)
    DESK_TIKTOK_ACTED: 'desk_tiktok_acted',                 // {op: download|copy|zip|posted|prepare, outcome, count, has_link} (#4649: which hand-overs he uses; prepare = Get it ready on TikTok, #5510; the post itself is server-side desk_tiktok_phone)
    DESK_TAB_VIEWED: 'desk_tab_viewed',                     // {tab: today|calendar|growth|search|kb|brief|work|system} (#3220: which Desk tabs he opens once the long sections left Today; #3224 split agents into work + system)
    DESK_RANK_EVIDENCE_OPENED: 'desk_rank_evidence_opened', // {kind: shorts|tutorials|next_up} (#5156: does he read the ranker's evidence; the full table or a card's "see every candidate")
    DESK_GOAL_STRIP_OPENED: 'desk_goal_strip_opened',       // {part: steps|month} (#5307: does he open the goal strip's detail; the numbers are server-side desk_goal_read)
    DESK_TOP3_OPENED: 'desk_top3_opened',                   // {rank, step} (#5307: does he act from the Top 3; the order is server-side desk_top3_computed)
    DESK_RANK_SWAP_OPENED: 'desk_rank_swap_opened',         // {kind: shorts} (#5156: how often he opens Swap on a ranked place; the swap itself is server-side desk_rank_override)
    DESK_GROWTH_SUB_VIEWED: 'desk_growth_sub_viewed',       // {sub: overview|posts|ads|astra|sessions} (#4524: which Growth sub-tabs he opens once Growth split; each open, not each re-read)
    DESK_SEARCH_SUB_VIEWED: 'desk_search_sub_viewed',       // {sub: overview|google|bing|indexing|answers|content} (#4565: which Search & SEO sub-tabs he opens; each open, not each re-read)
    DESK_KB_SUB_VIEWED: 'desk_kb_sub_viewed',               // {sub: check|ledger|coverage|collect|searches} (#4565: which KB accuracy sub-tab he opens; each open, not each re-read; coverage, collect and searches since #5186)
    DESK_SYSTEM_SUB_VIEWED: 'desk_system_sub_viewed',       // {sub: health|flags} (#5186: System health split into Health and Feature flags; each open)
    OPS_TAB_VIEWED: 'ops_tab_viewed',                       // {tab: desk|customers|outreach, via: click|link} (#5186: which Operations top tab opens, now that each has an address)
    OPS_CUSTOMERS_SUB_VIEWED: 'ops_customers_sub_viewed',   // {sub: book|feedback|credits} (#5186: Customers split into the book, Feedback and Credits; each open)
    ADMIN_REDIRECT_FOLLOWED: 'admin_redirect_followed',     // {from: flags|feedback|credits|knowledge_base|search_demand} (#5186: an old Admin address forwarded to its place in Operations; how often old links are still used)
    DESK_LIST_EXPANDED: 'desk_list_expanded',               // {list: sessions|routes|crawl|within_reach|tracked|pages, how: more|all|search} (#4564/#4568/#4569: which folded Desk lists he opens past their first rows, and how)
    DESK_REVIEW_FOLDED: 'desk_review_folded',               // {open} (#4570: does he open Today's folded Review results, or leave it folded)
    DESK_ROW_EXPANDED: 'desk_row_expanded',                 // {kind} (#3220: which Today rows he opens rather than acting from the line)
    DESK_FOCUS_MODE: 'desk_focus_mode',                     // {action: enter|exit|next|prev|approve|skip|details} (#3220: does Focus get used, and how)
    DESK_OVERLAY_TOGGLED: 'desk_overlay_toggled',           // {app: shorts|reels|tiktok|off} (#3229: does he check a Short against each app's safe zones before approving)
    DESK_PROOFREAD_OVERRIDE: 'desk_proofread_override',     // {kind} (#3339: how often the founder passes a claim code could not confirm)
    DESK_CALENDAR_APPROVED: 'desk_calendar_approved',       // {action_id: schedule|publish|skip, moved, outcome: scheduled|published|skipped|refused} (#3315: does he approve Shorts from the calendar, and does it land)
    DESK_CALENDAR_APPROVALS: 'desk_calendar_approvals',     // {control: only_on|only_off|next, count} (#3315: does the needs-approval toolbar get used)
    DESK_SHORT_WATCHED: 'desk_short_watched',               // {where: calendar|today} (#3315: the watch lock; does he play a Short through before approving)
    DESK_POSTS_INSIGHT_ACTED: 'desk_posts_insight_acted',   // {rule, who: desk|you, outcome: added|exists|refused} (#3405; exists = its card was already there: does he act on the Posts view's insights, and which rules earn a click)
    DESK_POSTS_ATTACHED: 'desk_posts_attached',             // {channel, outcome: attached|refused} (#3405: does he name the Desk post an unmatched read belongs to)
    DESK_POSTS_ROW_OPENED: 'desk_posts_row_opened',         // {kind, untracked} (#3405: which posts he opens for how people watched)
    DESK_POSTS_FILTERED: 'desk_posts_filtered',             // {control: channel|series|range|unmatched|sort|show_all|review|over_time_mode} (#3405: does the Posts table's controls get used; over_time_mode = Daily/Cumulative in the band, #3457)
    DESK_POSTS_OVER_TIME_OPENED: 'desk_posts_over_time_opened', // {channel} (#3457: does he open a channel card's over-time band, and for which channels)
    DESK_VIEWS_BY_DAY_VIEWED: 'desk_views_by_day_viewed',   // {window: all|28|7, variant} (#5545: does he read the views-by-day chart; once per open of Posts & channels)
    DESK_VIEWS_BY_DAY_WINDOW: 'desk_views_by_day_window',   // {window: all|28|7} (#5545: do the 28- and 7-day windows earn their place)
    DESK_VIEWS_BY_DAY_TABLE_OPENED: 'desk_views_by_day_table_opened', // {} (#5545: is the numbers table under the chart used)
    DESK_GROWTH_BY_DAY_VIEWED: 'desk_growth_by_day_viewed', // {window: all|90|28|7} (#5563: does he read the Overview's site visitors and new visitors by day; once per open)
    DESK_GROWTH_BY_DAY_WINDOW: 'desk_growth_by_day_window', // {window: all|90|28|7} (#5563: do the 90-, 28- and 7-day windows earn their place)

    // Activation funnel
    ONBOARDING_STARTED: 'onboarding_started',
    ONBOARDING_GRADE_PICKED: 'onboarding_grade_picked',
    ONBOARDING_PATH_CHOSEN: 'onboarding_path_chosen',      // {door: 'upload'|'questions'}
    ONBOARDING_COMPLETED: 'onboarding_completed',
    ONBOARDING_SKIPPED: 'onboarding_skipped',
    // Profile-first sign-up (#4702): /start shown {carried}, submitted
    // {colleges, test: sat|act|none, major: named|undecided}, and the list
    // read on screen {schools, read}. The server's first_list_read
    // (app_events) is the activation step; these say how the screen did.
    PROFILE_FIRST_SHOWN: 'profile_first_shown',
    PROFILE_FIRST_SUBMITTED: 'profile_first_submitted',
    LIST_READ_SHOWN: 'list_read_shown',
    // One college first (#5085, first_read_onboarding): shown gains {flow:
    // three_first|one_first} and list_read_shown {index, ms}; a step's button
    // succeeded {step: college|about_you, ms}; the button tapped with
    // something missing {field: gpa|test|score}; "Read these two" under the
    // first read {count}; the page left before any read landed {step, ms}.
    // Never a GPA, a score, a major or a school name.
    PROFILE_FIRST_STEP_DONE: 'profile_first_step_done',
    PROFILE_FIRST_BLOCKED: 'profile_first_blocked',
    FIRST_READ_MORE_ADDED: 'first_read_more_added',
    PROFILE_FIRST_LEFT: 'profile_first_left',
    // ADR 0009 Phase 3 — the 9th/10th story wizard, between grade-pick and
    // the existing upload-or-quick-questions door.
    ONBOARDING_STORY_STARTED: 'onboarding_story_started',
    ONBOARDING_STORY_SCREEN_COMPLETED: 'onboarding_story_screen_completed', // {screen}
    ONBOARDING_STORY_SKIPPED: 'onboarding_story_skipped',   // {screen}
    ONBOARDING_STORY_COMPLETED: 'onboarding_story_completed',
    QUICK_TOUR_COMPLETED: 'quick_tour_completed',          // {cta: 'profile'|'explore'}
    QUICK_TOUR_SKIPPED: 'quick_tour_skipped',              // {slide}

    // Redesign Phase 2 — the unified "Your Profile" surface.
    UNIFIED_PROFILE_VIEWED: 'unified_profile_viewed',          // {grade, tone, gaps}
    UNIFIED_PROFILE_GAP_ACTION: 'unified_profile_gap_action',  // {field, action: 'answer'|'upload'|'ask_astra'}
    UNIFIED_PROFILE_BULK_ACTION: 'unified_profile_bulk_action', // {action: 'upload'|'scratch'}
    PROFILE_DOCUMENT_UPLOADED: 'profile_document_uploaded',
    MAJOR_MAP_GENERATED: 'major_map_generated',
    COLLEGE_ADDED: 'college_added',
    FIT_ANALYSIS_RUN: 'fit_analysis_run',
    MAJOR_CHANCES_GENERATED: 'major_chances_generated',
    AGENT_CONNECTED: 'agent_connected',                    // Ask-in-Claude/ChatGPT launched
    CHECKLIST_ITEM_COMPLETED: 'checklist_item_completed',  // {item}

    // Acquisition (Google Ads key events — see marketing/google-ads-setup.md)
    // An emailed link opened while signed in: {campaign, source, path} (#2408).
    EMAIL_LINK_LANDED: 'email_link_landed',
    SIGNED_UP: 'signed_up',                                // first-ever sign-in (Firebase isNewUser)
    TRY_HERO_SEED_USED: 'try_hero_seed_used',              // example persona clicked: {seed}
    TRY_HERO_SUBMITTED: 'try_hero_submitted',              // anonymous landing hero: {chars}
    TRY_HERO_PREVIEW_SHOWN: 'try_hero_preview_shown',      // {fields}
    TRY_HERO_REFINED: 'try_hero_refined',                  // answered the GPA ask: {field}
    TRY_HERO_SCHOOL_CHECKED: 'try_hero_school_checked',    // "where do I stand at X": {school}
    TRY_HERO_SIGNIN_CLICKED: 'try_hero_signin_clicked',    // "keep it" CTA
    // Every try_hero_* event above (and TRY_HERO_COLLEGES_PROMPT) also carries
    // {from: 'college_page', school_id: <KB id>} when the visitor arrived from
    // a college page's "See your odds" link (#2608, utils/schoolEntry.js);
    // without one the props are exactly as listed.
    TRY_HERO_COLLEGES_PROMPT: 'try_hero_colleges_prompt',  // post-signup add-to-launchpad ask: {action: shown|added|skipped, count}
    // Astra Lite (#2549, ADR 0023): Astra runs the free read when astra_lite
    // is on (or in the founder's preview). The try_hero_* events above keep
    // their meaning on it; these add Astra's own steps. Never the words.
    ASTRA_LITE_SHOWN: 'astra_lite_shown',                  // the Astra free read rendered: {}
    ASTRA_LITE_MIC: 'astra_lite_mic',                      // {state: started|blocked}
    ASTRA_LITE_TIDY: 'astra_lite_tidy',                    // the on-device Tidy up: {state: offered|none|used|kept}
    ASTRA_LITE_CLARIFY: 'astra_lite_clarify',              // a sanity question: {check, state: asked|answered, answer: choice id|number}
    ASTRA_LITE_QUESTION_ASKED: 'astra_lite_question_asked', // {source: chip|typed, counted, redirected, left} (never crisis, #4294)
    ASTRA_LITE_LIMIT_REACHED: 'astra_lite_limit_reached',  // the third counted question answered: {}
    ASTRA_LITE_START_FREE: 'astra_lite_start_free',        // Start free on the limit card: {schools}
    // #3480 (astra_read_unlock, founder pick B): the box under a locked read.
    // shown once per school read; clicked -> signin_started {entry:
    // astra_read_unlock} -> signin_completed -> astra_carry_imported {read}.
    // The server also counts unlock_shown / read_unlocked (aggregate only).
    ASTRA_READ_UNLOCK: 'astra_read_unlock',                // {state: shown|clicked, schools_n, from?: card|chip}
    // Sign-up moments (#5195, founder pick A, behind signup_moments): the ask
    // right under what a guest just got. Shown once per moment per page view;
    // clicked -> signin_started {entry: moment_*} -> signed_up {entry} and
    // the server's account_created {signup_moment}. `followed`: the Targets
    // link on /start after a moment's sign-up. Never the GPA, the state or a
    // school name; the Targets count only as a bucket.
    SIGNUP_MOMENT: 'signup_moment',                        // {moment: chances_result|astra_read|long_read|comparing, state: shown|clicked|dismissed|followed, payoff: targets|why|both|bands|figure, targets_n_bucket?: 0|1-5|6-20|21+, band?, fixed_band?: bool, schools_n?, variant: 'A'}
    // Astra's open chat (#2725, founder pick B): the landing hero. Topics are
    // the closed list the Desk counts; no event carries a typed word.
    ASTRA_CHAT_SHOWN: 'astra_chat_shown',                  // the hero chat rendered: {variant}
    ASTRA_CHAT_TOPIC_TAB: 'astra_chat_topic_tab',          // a topic tab tapped, before asking: {topic}
    ASTRA_CHAT_SEED_TAPPED: 'astra_chat_seed_tapped',      // a seed question tapped: {seed_id, topic, position}
    // Popular questions inside Astra (#5258): the "most asked" rows, counted
    // labels only, never words. Shown once per render with chips; tapped with
    // its topic and rank; the insight line once when it shows. The question
    // that follows keeps its own event (source / from: popular).
    ASTRA_POPULAR_SHOWN: 'astra_popular_shown',            // {surface: lite|sheet|astra, basis: week|students, n}
    ASTRA_POPULAR_TAPPED: 'astra_popular_tapped',          // {surface, topic, rank}
    ASTRA_INSIGHT_SHOWN: 'astra_insight_shown',            // {surface, kind: topic}
    ASTRA_CHAT_QUESTION_ASKED: 'astra_chat_question_asked', // answered: {source: seed|typed|band, topic, depth, counted, left, preview, scope?: college_page|ask_link|news, school_id?, story? (news)}
    ASTRA_CHAT_SOURCE_CLICKED: 'astra_chat_source_clicked', // a source chip or our page: {kind, topic}
    ASTRA_CHAT_PERSONALIZE: 'astra_chat_personalize',      // "Make it about me": {state: offered|started|not_now, topic, entry: card|header|nudge, school_id} (card: #3262; nudge: #5229, preset by name, so no id)
    // The nudge after the first useful answer. `read` (#5229): the box led
    // with the free read (a school was named and the topic fits); `cta` on a
    // click: the read button (opens Make it about me) or the sign-in line
    // (then signin_started {entry: astra_chat_nudge_<topic>}).
    ASTRA_CHAT_NUDGE: 'astra_chat_nudge',                  // {topic, state: shown|clicked, read: bool, cta?: read|signin}
    ASTRA_CHAT_LIMIT: 'astra_chat_limit',                  // the daily limit card: {state: shown|start_free, topics_n}
    ASTRA_CHAT_FAILED: 'astra_chat_failed',                // the question came back with an error (#3056): {reason: busy|network|server, status}
    ASTRA_CHAT_START_OVER: 'astra_chat_start_over',        // the kept conversation cleared (#2809): {}
    ASTRA_CHAT_BACK: 'astra_chat_back',                    // Back to Astra on a college page (#2809): {}
    // Astra from a college page or a Short (#3262, founder pick A of the
    // Astra entry mocks, 2026-10-06). The funnel: college page or Short ->
    // a question -> the personal read -> sign-in, read as
    // college_ask_shown / ask_link_landed -> astra_sheet_opened ->
    // astra_chat_question_asked {scope, school_id} -> astra_chat_personalize
    // {entry: 'card'} -> signin_started / signed_up.
    COLLEGE_ASK_SHOWN: 'college_ask_shown',                // the "Ask Astra about <School>" box half on screen, once: {slug, school_id}
    COLLEGE_ASK_OPENED: 'college_ask_opened',              // a seed or a typed question from the box: {slug, school_id, entry: seed|typed, seed_id}
    // "Where do I stand at <School>?" under a college page's fact tiles (#3885,
    // founder pick A of the sign-up mocks, 2026-10-11). The funnel: does a
    // Google visitor who sees the card check their grades, get a read, press
    // save, and arrive signed in with it? Ids, enums and booleans only; the
    // GPA and state chosen never ride an event.
    COLLEGE_STAND_SHOWN: 'college_stand_shown',            // the card half on screen, once: {slug, school_id, landing_layout?, landing_answer? (a paid landing, #4476)}
    COLLEGE_STAND_CHECK: 'college_stand_check',            // {state: requested|shown|failed, slug, school_id, grade: 11|12, in_state, band? (shown), reason? (failed)}
    COLLEGE_STAND_SAVE: 'college_stand_save',              // {state: clicked|signed_in, slug, school_id, dated? (clicked: a reminder date was shown), read_carried? (signed_in)}
    ASTRA_SHEET_OPENED: 'astra_sheet_opened',              // Astra's sheet opened over a college page: {slug, school_id, via: seed|typed|ask_link|hash}
    // A landing from a paid ad on a college page (#4088, the Deadline-Season
    // Ads Plan's bounce rule: does a paid visitor stay 10 seconds?). Fired
    // once per page load when the URL carries a plan campaign or an ad click
    // id; stayed fires once if the page is still open and visible after
    // 10 seconds. {slug, school_id, campaign (deadline-* or null), source
    // (utm_source tag or null), paid_click}. Ids and tags only.
    AD_LANDING_SEEN: 'ad_landing_seen',
    AD_LANDING_STAYED: 'ad_landing_stayed',
    // The paid first screen (#4476, founder pick A): the ad's question, its
    // sourced answer, then the stand card. Fired once per landing when that
    // screen is drawn: {slug, school_id, layout: 'a', answer (the ad's
    // utm_term key, or 'generic'), term (the URL's utm_term tag or null),
    // campaign, source, medium, orientation: portrait|landscape}. The stand
    // card's own events (college_stand_shown/check/save) carry
    // landing_layout and landing_answer on that page, so its views and checks
    // read per landing and per answer.
    AD_LANDING_BLOCK_SHOWN: 'ad_landing_block_shown',
    ASK_LINK_LANDED: 'ask_link_landed',                    // /ask/<short> arrived: {short, slug, resolved, utm_source, utm_medium, utm_campaign}
    // The landing conversation brought into the account at sign-in (#2945).
    // Counts and closed reasons only, never a word the student typed.
    ASTRA_CARRY_IMPORTED: 'astra_carry_imported',          // saved as her first thread: {questions, schools_n, read: bool (#3480)}
    ASTRA_CARRY_UNDONE: 'astra_carry_undone',              // the notice's buttons: {step: start_fresh|undo, questions}
    ASTRA_CARRY_FAILED: 'astra_carry_failed',              // not saved: {reason: under_13|empty|assent|refused|network|write_failed|...}
    SIMILAR_SCHOOLS_OPENED: 'similar_schools_opened',      // {from, major, peer_count, thin}
    SIMILAR_SCHOOLS_PEER_ADDED: 'similar_schools_peer_added', // {from, added, door}

    // Monetization funnel
    UPGRADE_VIEWED: 'upgrade_viewed',
    SIGNIN_STARTED: 'signin_started',                      // {method: google|email, flow: popup|redirect|in_app_browser (google) or sheet|in_app_browser|back (email, fired when the link is asked for), entry, app?} — vs signin_completed = abandonment; entry: content/landingIds.js SIGNIN_ENTRIES, else 'other'
    SIGNIN_COMPLETED: 'signin_completed',                  // any successful sign-in (new or returning): {method: google|email|email_code (the Home Screen app's code, #4189), device?: same|other (email: the browser that asked, or another)}
    // Email-link sign-in (#3928). Is the link getting out, and how often is
    // it opened somewhere other than the browser that asked (the Instagram
    // case)? Counts and enums only; never the address.
    SIGNIN_LINK_SENT: 'signin_link_sent',                  // {method: 'email', flow: sheet|in_app_browser|back, entry, app?, resend: bool, code?: true (sent with a Home Screen code, #4189)}
    SIGNIN_LINK_OTHER_DEVICE: 'signin_link_other_device',  // {} the link opened where no request was kept: the email is asked for again
    SIGNIN_LINK_HOME_SCREEN: 'signin_link_home_screen',    // {} a link the iPhone Home Screen app asked for opened in Safari: "Finish in your Home Screen app" shown, link not used (#4189)
    SIGNIN_SAFARI_INSTEAD: 'signin_safari_instead',        // {} ... and the student chose "Sign in here in Safari instead" (#4189)
    // The sign-in funnel end to end (#5084). Before these the sheet itself
    // was invisible: opening it fired nothing. Every signin_* event also
    // carries browser_class (ios_safari|in_app|other, signInTrouble.
    // signInBrowserClass: the function that picks the sheet) and attempt_id
    // (a random id made when the sheet opens, carried through the email
    // link, so a finish in another browser still joins). Never an address.
    SIGNIN_SHEET_OPENED: 'signin_sheet_opened',            // {mode: choose|email_first|in_app|back, browser_class, app?, entry, attempt_id}
    SIGNIN_SHEET_CLOSED: 'signin_sheet_closed',            // {mode, browser_class, step: start|email|sent, attempt_id} closed by the student (close, backdrop, Escape), not by choosing Google
    SIGNIN_IN_APP_WAITED: 'signin_in_app_waited',          // {waited_ms, outcome: email_on|email_off|timeout, browser_class, attempt_id} an app's-browser sheet opened before get-auth-methods answered; once, at the answer or at a send that stopped waiting (2.5 s)
    // More ways to sign in (#5154). `method` on every signin_* event gains
    // apple and facebook; signin_sheet_opened adds platform (ios|android|
    // desktop|unknown) and providers (the order drawn, e.g. apple,google);
    // signin_started adds platform and position (1-3 among the buttons);
    // signin_completed adds platform and relay (Apple only, a boolean).
    SIGNIN_ACCOUNT_EXISTS: 'signin_account_exists',        // {method: the provider tapped, existing: unknown (never looked up: enumeration), browser_class, attempt_id}
    SIGNIN_LINKED: 'signin_linked',                        // {added: apple|facebook, outcome: linked|declined|failed} the consent after an account-exists sign-in
    SIGNIN_SECOND_ACCOUNT_HINT: 'signin_second_account_hint', // {method: apple|facebook, action: shown|tapped} the "Used Stratia before?" line on a new relay/Facebook account
    ACCOUNT_DELETE_APPLE_REVOKE: 'account_delete_apple_revoke', // {outcome: revoked|cancelled|failed} the Apple step of Delete my account
    PAYWALL_SHOWN: 'paywall_shown',                        // {feature, credits_remaining} credits modal opened
    UPGRADE_CTA_CLICKED: 'upgrade_cta_clicked',            // {surface} any upgrade/get-credits button
    // A Season Pass prompt was SEEN (#2427). Before this only the clicks were
    // counted, so the fit read's 0 clicks had no denominator. Once per
    // impression; pair with upgrade_cta_clicked {surface} and
    // upgrade_viewed.from_surface (the CTA links /pricing?from=<surface>).
    SEASON_MOMENT_SHOWN: 'season_moment_shown',            // {surface: fit_read_verdict}
    NUDGE_CLICKED: 'nudge_clicked',                        // {nudge} free-credit framing engagement
    CHECKOUT_STARTED: 'checkout_started',                  // {product_id, amount}
    CHECKOUT_CANCELLED: 'checkout_cancelled',              // {product_id} returned via Stripe cancel_url
    CREDITS_PURCHASED: 'credits_purchased',                // fired via trackPurchase()
    SUBSCRIPTION_STARTED: 'subscription_started',          // fired via trackPurchase()
    // The 7-day reverse trial (#4703). The start and end are server events
    // (app_events trial_started / trial_ended); these are what the student saw.
    TRIAL_NOTE_SHOWN: 'trial_note_shown',                  // {surface: note|strip} the trial's terms (note) or one line (strip) rendered
    TRIAL_CAP_SHOWN: 'trial_cap_shown',                    // {feature} the trial's internal ceiling met inside the 7 days
    TRIAL_DAY8_WALL_SHOWN: 'trial_day8_wall_shown',        // {feature} day 8's first new deep request met the wall
    // "Send this to a parent" (#4704; the one-tap sheet #5811): {from:
    // fit_read|cost_chart|aid_index|aid_card|wall|share_page, state:
    // shown|opened|sent|dismissed}; flag off, the wall's tap sends {from: 'wall'}.
    PARENT_SHARE_OPENED: 'parent_share_opened',
    // The parent path (#5811): who the account is for, on /start
    // {role: student|parent|skipped}; a parent-role account's home opened {}.
    // The stored role is the server's account_role_set (app_events).
    SIGNUP_ROLE_ANSWERED: 'signup_role_answered',
    PARENT_HOME_OPENED: 'parent_home_opened',
    // #4747: Send / Don't send her scores at a test-optional school, from the
    // card (the server's score_sending_set counts every door, Astra included).
    SCORE_SENDING_SET: 'score_sending_set_card',           // {send, needs_update}
    // The parent read (#4704): the page opened by a parent (not a preview).
    // The share itself is the server's parent_share_sent (app_events).
    PARENT_READ_OPENED: 'parent_read_opened',              // {from?, can_buy} (#5811: the moment the send came from)
    CREDITS_EXHAUSTED: 'credits_exhausted',                // {feature} blocked at zero credits — the conversion-pressure moment
    // The pricing redesign (Free + Season Pass, #2343): which card's button
    // is pressed, before any checkout exists. checkout_started only fires
    // once Stripe answers, so the Free card and the Patent Lab add-on (a
    // link, no checkout) were invisible (#2371).
    PRICING_PLAN_CLICKED: 'pricing_plan_clicked',          // {plan, signed_in, action: checkout|free|link|reactivate|switch}
    // Through Graduation on /pricing (#5207): drawn for a signed-out visitor
    // too, so the funnel "shown -> chosen -> asked the year -> checkout" reads
    // signed out and in. known_year: the server's own year was on the card
    // (false: shown before the server knew the grade). The signed-out view
    // and choice are also counted server-side (profile_manager_v2
    // graduation_pricing.py), and checkout_started carries
    // chosen_signed_out (server: through_graduation_checkout_started).
    GRADUATION_TERM_SHOWN: 'graduation_term_shown',        // {surface: pricing, signed_in, known_year}
    GRADUATION_TERM_CHOSEN: 'graduation_term_chosen',      // {term: season|graduation, signed_in, known_year}
    GRADUATION_YEAR_ASKED: 'graduation_year_asked',        // {step: shown|saved} checkout found no grade on file
    GRADUATION_TERM_REFUSED: 'graduation_term_refused',    // {reason: the server's enum, signed_in_here} checkout or the offer said no
    // The landing page's Patent Lab proof band (#2348): its two doors.
    PATENT_LAB_PROOF_CLICKED: 'patent_lab_proof_clicked',  // {cta: season_pass|paper}
    // The Desk's landing watch (#2512): what visitors reach, click and watch
    // on the landing page, with ids from content/landingIds.js and never an
    // element's text (autocapture text leaked student data, #2511). Each
    // answers one question the Growth cards ask: which sections do phone
    // visitors ever reach, which doors get used, which Build step draws
    // interest. (The film's progress event left with the film band, #2513:
    // /#film now opens the file itself, which no page event can measure.)
    LANDING_SECTION_SEEN: 'landing_section_seen',          // {section, order} once per page view (useLandingInstrumentation)
    LANDING_CTA_CLICKED: 'landing_cta_clicked',            // {cta_id, section}
    LANDING_BUILD_TILE_CLICKED: 'landing_build_tile_clicked', // {tool: brainstorm|patent|research|pitch}
    // The band's visitor maps (#4607): does anyone look, and do phone
    // visitors swipe to the world? Never a place code in the props.
    LANDING_MAP_VIEWED: 'landing_map_viewed',              // {maps: us|us_world} once per page view
    LANDING_MAP_PANEL_SWITCHED: 'landing_map_panel_switched', // {panel: us|world, via: tab|swipe}

    // Billed AI actions (1 credit each, charged server-side). Each fires on the
    // spend, so credits_purchased -> <action> closes the value loop.
    MAJOR_STRATEGY_GENERATED: 'major_strategy_generated',  // {university_id}
    ESSAY_FEEDBACK_REQUESTED: 'essay_feedback_requested',  // {university_id, draft_words, prompt_index} — never the draft (prompt_index: #5336)

    // Scratch-pad ingestion (#767)
    SCRATCHPAD_PROCESSED: 'scratchpad_processed',          // {chars, fields_extracted}
    SCRATCHPAD_SAVED: 'scratchpad_saved',                  // {fields_saved}

    // Engagement / retention signals
    ROADMAP_TASK_ADDED: 'roadmap_task_added',

    // Paying for college (#4154). Step changes are captured server-side as
    // aid_step_set {step, status} (profile_manager_v2/app_events.py), the one
    // place every door reaches; these are the browser-only moments.
    AID_TRACK_VIEWED: 'aid_track_viewed',             // {total_steps, done_steps, css_schools, css_no_date}
    AID_ASK_ASTRA_OPENED: 'aid_ask_astra_opened',     // {where: header|fafsa}
    AID_SCHOOL_PAGE_OPENED: 'aid_school_page_opened', // {kind: dated|no_date} a school's aid page from the card
    AID_PARENT_SHARE_OPENED: 'aid_parent_share_opened', // "Send to a parent" -> /share
    RESEARCH_SAVED: 'research_saved',

    // The Builder track (#2296): counts, ids and enums only — never an answer
    // or a sentence. Answers an agent saves never pass through this page;
    // the server captures every save as patent_answer_recorded {via} (#2371).
    BUILDER_PAGE_VIEWED: 'builder_page_viewed',            // {step}
    BUILDER_INTERVIEW_STARTED: 'builder_interview_started', // {via}
    BUILDER_QUESTION_OPENED: 'builder_question_opened',    // {question_id} an open question handed to Astra
    BUILDER_ANSWER_SAVED: 'builder_answer_saved',          // {question_id} typed on the page's questionnaire (ADR 0016)
    BUILDER_QUESTION_HELP: 'builder_question_help',        // {question_id} a stuck question handed to Astra
    BUILDER_DRAFTED: 'builder_drafted',                    // {version, open_questions}
    BUILDER_PRIOR_ART_ADDED: 'builder_prior_art_added',    // {via}
    BUILDER_EXPORTED: 'builder_exported',                  // {}
    BUILDER_SPECIFICATION_REQUESTED: 'builder_specification_requested', // {outcome: queued|already|season_pass|not_ready|in_progress|queue_down|failed}
    BUILDER_SPECIFICATION_READY: 'builder_specification_ready',         // {state: ready|needs_answer|failed, open_questions} once per arrival
    BUILDER_SPECIFICATION_OPENED: 'builder_specification_opened',       // {kind: html|specification_pdf|drawings_pdf}
    // The patent application package (2026-09-22; $199, #3053): the priced ask shown once per
    // load, and the checkout tap with its outcome.
    BUILDER_PACKAGE_SHOWN: 'builder_package_shown',         // {}
    BUILDER_PACKAGE_CHECKOUT: 'builder_package_checkout',   // {outcome: redirecting|season_pass|failed}
    BUILDER_FILING_RECORDED: 'builder_filing_recorded',     // {} the family typed the receipt's two facts; never the number
    BUILDER_FILING_TO_APPLICATION: 'builder_filing_to_application', // {part: activity|roadmap, outcome: added|already|failed} never the number or title
    BUILDER_NEXT_STEP: 'builder_next_step',                 // {step} the next-step card's button (pick A): which step the student acted on
    BUILDER_SPEC_ANSWERS_SAVED: 'builder_spec_answers_saved', // {groups, left_open} counts only: answers added on the specification card (pick D) and asks left open; never the words
    BUILDER_QA_OPENED: 'builder_qa_opened',                 // {} the "Questions and answers" record opened
    // The prior-art search (design 2026-09-22): the funnel question is
    // "did they come back and review what the worker found".
    BUILDER_SEARCH_REQUESTED: 'builder_search_requested',   // {outcome: queued|already|season_pass|not_ready|in_progress|limit|queue_down|failed, again}
    BUILDER_SEARCH_READY: 'builder_search_ready',           // {state: ready|none_found|failed, matches} once per arrival
    BUILDER_MATCH_OPENED: 'builder_match_opened',           // {kind} the record page opened in a new tab
    BUILDER_MATCH_REVIEWED: 'builder_match_reviewed',       // {decision: accepted|set_aside|undo}
    // The Season Pass wall on the Builder (ADR 0013). `at` is where it was
    // met: door | interview_done | writeup | export. These two are the funnel that
    // says whether the draft wall converts, and whether mock C is needed.
    BUILDER_SEASON_PASS_SHOWN: 'builder_season_pass_shown',     // {at, steps} at: door|study_door|pitch_door|pack_door; steps: the Builder pack door's length (4)
    BUILDER_SEASON_PASS_CLICKED: 'builder_season_pass_clicked', // {at, steps}
    // The Builder pack home (#2389, founder pick A 2026-09-29). Funnel: does a
    // student who opens the home start the seven, finish them, and go on to a
    // lab from the path? `state` is the first step not yet done (or done).
    BUILDER_PACK_HOME_VIEWED: 'builder_pack_home_viewed',           // {steps: 3|4, state: brainstorm|patent|research|pitch|done|door} once per page open
    BUILDER_PACK_SESSION_STARTED: 'builder_pack_session_started',   // {} the first of the seven saved from the home
    BUILDER_PACK_SESSION_COMPLETED: 'builder_pack_session_completed', // {} the seventh saved from the home
    BUILDER_PACK_PATH_CLICKED: 'builder_pack_path_clicked',         // {lab: brainstorm|patent|research|pitch} a path step followed
    // A season door (Conrad, STS, the journals, Diamond) put on the roadmap
    // from the page it belongs to (#2398). Fires on an answered write only.
    BUILD_DOOR_ADDED_TO_ROADMAP: 'build_door_added_to_roadmap',     // {door: conrad|sts|journals|diamond}
    // The Pitch Lab (#2358). Funnel: does a student who opens the lab write a
    // pitch, practice it out loud, and use what a run suggested? Ids, enums
    // and counts only; never a slide, a script line or a transcript.
    PITCH_PAGE_VIEWED: 'pitch_page_viewed',                 // {step}
    PITCH_GENERATED: 'pitch_generated',                     // {outcome: queued|written|failed|refused|limit|in_progress|season_pass, length_min} queued at the ask; written/failed when the research job lands
    PITCH_EXPORTED: 'pitch_exported',                       // {format: pptx|pdf|pdf_notes}
    PITCH_SLIDE_EDITED: 'pitch_slide_edited',               // {slide}
    PITCH_RUN_RECORDED: 'pitch_run_recorded',               // {outcome: ready|failed|refused|limit|upload_failed, seconds}
    PITCH_PROPOSAL_APPLIED: 'pitch_proposal_applied',       // {slides} count of slides changed
    PITCH_MIC_DENIED: 'pitch_mic_denied',                   // {} the permission prompt was refused
    // #2371: the practice funnel's two missing steps. Started is the
    // denominator for pitch_run_recorded (a start with no run is a student
    // who stopped mid-pitch); again says whether the feedback sends them back.
    PITCH_PRACTICE_STARTED: 'pitch_practice_started',       // {slides, retry} recording began
    PITCH_PRACTICE_AGAIN: 'pitch_practice_again',           // {runs} "practice again" from the feedback
    // The File it walk's links out to the USPTO (#2371): does the family
    // actually walk the steps, and which one do they stop at.
    BUILDER_FILING_LINK_CLICKED: 'builder_filing_link_clicked', // {step}
    // The Research Lab (#2378). The client-side funnel: does a student who
    // opens the lab reach the question, and do they leave with the paper and
    // the log in hand. Every route's completion (started, area_picked,
    // question_locked, pointers, reviews, submission) is a SERVER-side
    // study_* event in lab_events.py; never the student's words here.
    RESEARCH_LAB_OPENED: 'research_lab_opened',             // {step: door|compact|area|...|defense} once per page open
    STUDY_AREA_OWN_WRITTEN: 'study_area_own_written',       // {} the student wrote their own area instead of picking one
    STUDY_EXPORT_CLICKED: 'study_export_clicked',           // {format: pdf} the paper export
    STUDY_LOG_EXPORTED: 'study_log_exported',               // {} the interaction log saved as markdown
    // Steps that COMPLETE on the server (a specification delivered, a search
    // finished, a pitch researched, a run reviewed) and everything an agent
    // does are captured SERVER-side as patent_* / pitch_* events by
    // cloud_functions/profile_manager_v2/lab_events.py (#2371), with
    // emitted_by: 'server'. Those names never equal a name here (drift test
    // in test_server_analytics.py), so one action never counts twice.
    OUTCOME_LOGGED: 'outcome_logged',                      // decision ledger
    VALUE_SPOTLIGHT_EXPANDED: 'value_spotlight_expanded',  // {surface}
    ESSAY_DRAFT_SAVED: 'essay_draft_saved',                // {university_id} — never the text
    // RETIRED 2026-10-03 (#2997): CHAT_MESSAGE_SENT ('chat_message_sent') had
    // no call site left; Astra's turns are CONCIERGE_MESSAGE_SENT. Do not
    // reuse the name for a different action.
    // RETIRED 2026-10-09 (#3615): STRATEGY_REPORT_VIEWED, STRATEGY_REPORT_EXPORTED,
    // STRATEGY_BRIEF_VIEWED and STRATEGY_BRIEF_MOVE_CLICKED went with the
    // Strategy Report page (founder: "strategy brief is retired"). Do not reuse
    // the names for a different action.
    // Launchpad readiness. Counts and ids only —
    // never the pointer's text, which is the student's own gap.
    READINESS_ROADMAP_OPENED: 'readiness_roadmap_opened',   // {university_id} card → that school's roadmap slice
    READINESS_ACTION_ADOPTED: 'readiness_action_adopted',   // {university_id, kind} a pointer became a tracked task
    // Season moments (#2144). The moment key only —
    // the funnel question is "how many families reach each rung, and when".
    // Fired once per (account, moment) per browser; the server holds the
    // first-reached date, this is the client-side sighting of it.
    SEASON_MOMENT_REACHED: 'season_moment_reached',         // {moment}
    DECISION_CARD_SHARED: 'decision_card_shared',           // {method, named_school}

    // Book the Visit (2026-09-03) — the direct-link redesign of Visits &
    // Events. Opening the school's own page proves nothing was scraped
    // wrong; the self-report closes the loop back to the roadmap.
    VISIT_LINK_CLICKED: 'visit_link_clicked',              // {university_id, has_direct_portal}
    ROADMAP_VISIT_REPORTED: 'roadmap_visit_reported',      // {university_id, source: 'direct_link'}
    RESOURCE_CTA_CLICKED: 'resource_cta_clicked',          // {slug} paper → Try Stratia
    // News (#2575). Page views ride $pageview; these answer "does a news
    // reader open the story, check its sources, read on, and start?"
    NEWS_STORY_OPENED: 'news_story_opened',                // {slug, from: 'index' | 'list_box' | 'college_page' | 'launchpad_card' | 'more_news'} (#3972: a school's story from its college page or its Launchpad card; #5009: a story page's More news)
    NEWS_SOURCE_CLICKED: 'news_source_clicked',            // {slug, host}
    NEWS_READ_NEXT_CLICKED: 'news_read_next_clicked',      // {slug, to} the guide's slug
    NEWS_CTA_CLICKED: 'news_cta_clicked',                  // {slug, signed_in}
    NEWS_ASK_SHOWN: 'news_ask_shown',                      // the story's "Ask Astra" box half on screen, once (#3272): {story, signed_in}
    NEWS_ASK_OPENED: 'news_ask_opened',                    // a seed, a typed question or the school's /ask link from it: {story, seed_id, school_id, entry: seed|typed|page_link}
    // Finding a story on /news (#3931): the topic and the result count only,
    // NEVER the typed words (a search can carry a name or a school a student
    // has not told anyone about).
    NEWS_SEARCHED: 'news_searched',                        // a search settled (typing paused): {topic: <chip id>|'all', results}
    NEWS_TOPIC_PICKED: 'news_topic_picked',                // a topic chip tapped: {topic: <chip id>|'all', results}
    NEWS_SHOW_OLDER: 'news_show_older',                    // "Show 20 older stories": {shown, total}
    NEWS_GUIDE_SUGGESTED_OPENED: 'news_guide_suggested_opened', // the no-results guide link: {topic: <chip id>|'none', to}
    // Public college pages (#2579). Source clicks ride SOURCE_LINK_CLICKED
    // {source, surface: 'college-page'}; this answers "does a college page
    // send readers into the free read?"
    COLLEGE_ODDS_CLICKED: 'college_odds_clicked',          // {slug}
    // The Watch section (#4608): does a college page send readers to our
    // videos, and which places do they pick? seen fires once per page view
    // when the section is half on screen.
    COLLEGE_VIDEOS_SEEN: 'college_videos_seen',            // {slug, count}
    COLLEGE_VIDEO_CLICKED: 'college_video_clicked',        // {slug, video: <id>|null, channel, placement: 'poster'|'watch'|'also'|'follow'}
    // The /videos page (#4728, founder "A1" 2026-10-18): does the landing
    // ribbon's "Watch them" bring people to the videos, which do they play
    // (each tap opens the video on its platform, as on the college pages), and
    // do they go on to a college page or Astra? Never the typed search.
    RIBBON_WATCH_CLICKED: 'ribbon_watch_clicked',          // {milestone} the ribbon's "Watch them" on the landing page
    VIDEOS_PAGE_VIEWED: 'videos_page_viewed',              // {count: videos listed | null when the read failed}
    VIDEO_PLAYED: 'video_played',                          // {slug, video, channel, placement: 'poster'|'watch'|'also'|'top_stat'|'top_tap_out', source: 'videos_page'} a tap OUT to a platform
    VIDEOS_FILTERED: 'videos_filtered',                    // {theme: <chip>|'All', searched, matches} on a chip, or leaving a non-empty search
    VIDEOS_COLLEGE_CLICKED: 'videos_college_clicked',      // {slug, placement: 'header'|'card'|'top_player', play_id?} on to the college page
    VIDEOS_ASK_CLICKED: 'videos_ask_clicked',              // {placement: 'middle'|'end'|'top_player', play_id?} the Ask Astra button
    // The most-watched Shorts on /videos (#5433, founder pick B): does the
    // visitor reach the list, play a Short here (from the list or swiping on),
    // watch it through? One play per distinct play_id, which the first-party
    // video-event counter also records (the counter is the plays record).
    TOP_SHORTS_SEEN: 'top_shorts_seen',                    // {count, as_of_age_h} once per visit, when the list scrolls into view
    TOP_SHORT_PLAYED: 'top_short_played',                  // {slug, video, rank, placement: 'top_list'|'top_next', play_id}
    TOP_SHORT_PROGRESS: 'top_short_progress',              // {video, rank, pct: 25|50|75|100, play_id}
    COLLEGE_LINK_CLICKED: 'college_link_clicked',          // {from: home|resources, to: slug|all, position (0 for See all)} Look up a college (#4434 plan A): do these links bring people to the college pages?
    COLLEGE_LINKS_EXPANDED: 'college_links_expanded',      // {from} the phone's "Show 12 more colleges"
    COLLEGE_SIMILAR_CLICKED: 'college_similar_clicked',    // {slug, to, position} a Similar schools card (#2895): do families move between college pages?
    COLLEGE_MAJORS_FILTERED: 'college_majors_filtered',    // {slug, matches} on leaving a non-empty filter; never the typed text
    COLLEGES_INDEX_FILTERED: 'colleges_index_filtered',    // {state, sort, searched, matches} on a state or sort change, or leaving a non-empty search; never the typed text (#2650)

    // Does anyone actually click a citation? This is the measurement that
    // validates (or kills) the "cite every number" investment. `source` is the
    // slugged citation label — an enum from the call site, never user text.
    SOURCE_LINK_CLICKED: 'source_link_clicked',            // {source, surface}

    // Same measurement, for the regulation citations added to privacy
    // touchpoints (#117 follow-on, 2026-08-17): does anyone actually check
    // the law behind a promise, or is it decoration nobody clicks.
    REGULATION_CITATION_CLICKED: 'regulation_citation_clicked', // {law, surface}

    // University profile — which data students actually want.
    UNIVERSITY_TAB_VIEWED: 'university_tab_viewed',        // {tab, university_id} on tab change
    NET_PRICE_VIEWED: 'net_price_viewed',                  // {university_id, brackets, has_debt}
    // The add-to-list ask on the university page (
    // variant G2). Shown is the denominator: without it a click-through rate
    // is a number about nothing. `college_added` (fired by the add call) is
    // the third step of the same funnel.
    UNIVERSITY_UNLOCK_BAND_SHOWN: 'university_unlock_band_shown',   // {university_id, sections}
    UNIVERSITY_UNLOCK_ADD_CLICKED: 'university_unlock_add_clicked', // {university_id}

    // Summer Programs (#462)
    SUMMER_PROGRAMS_VIEWED: 'summer_programs_viewed',      // {programs, recommended, rising_grade}
    SUMMER_PROGRAM_OPENED: 'summer_program_opened',        // {program_id}
    SUMMER_PROGRAM_ADDED: 'summer_program_added',          // {program_id, action: roadmap|activity}

    // Activity Ideas — the free advisory surface, and whether its suggestions
    // actually get ACTED on. `category` is the taxonomy enum, never the
    // LLM-written title/why/first_step.
    ACTIVITY_IDEAS_VIEWED: 'activity_ideas_viewed',        // {count} a generated set on screen
    ACTIVITY_IDEAS_GENERATED: 'activity_ideas_generated',  // {forced, count} forced = Refresh
    ACTIVITY_IDEA_ADDED: 'activity_idea_added',            // {category, action: roadmap|activity}
    ACTIVITY_IDEA_SUMMER_LINK_CLICKED: 'activity_idea_summer_link_clicked', // {category}
    // The essay prompts Astra named, carried into the workshop (#2249).
    ESSAY_PICKER_OPENED: 'essay_picker_opened',                   // {count, offered}
    ESSAY_PICKER_STARTERS: 'essay_picker_starters',               // {asked, made, limited}

    // Honest testimonials (real, consented quotes only — never fabricated)
    TESTIMONIAL_PROMPTED: 'testimonial_prompted',          // {context} collection prompt shown
    TESTIMONIAL_SUBMITTED: 'testimonial_submitted',        // {context, chars} — never the quote text

    // RETIRED 2026-08-15: FIT_RATIONALE_OPENED ('fit_rationale_opened') counted
    // clicks on Discover's "Why this band?" toggle. The founder's Variant-3 pick
    // made the reasoning always-visible, so the click it measured no longer
    // exists. Do NOT resurrect the name on render — an event that fires once per
    // card drawn is a different measurement wearing the old name, and it would
    // silently corrupt every historical comparison in PostHog. Whether the
    // reasoning persuades is now measured downstream, at the add (see
    // UNIVERSITY_UNLOCK_ADD_CLICKED / COLLEGE_ADDED).

    // Proactive in-context feedback ask (👍/👎 beneath an AI result)
    FEEDBACK_FORM_SUBMITTED: 'feedback_form_submitted',   // {rating, context, has_text}
    FEEDBACK_PROMPT_SHOWN: 'feedback_prompt_shown',        // {surface}
    FEEDBACK_PROMPT_RATED: 'feedback_prompt_rated',        // {surface, verdict: up|down}
    FEEDBACK_PROMPT_COMMENTED: 'feedback_prompt_commented',// {surface, verdict} — NEVER the free text

    // The credit-spend payoff, viewing side (generation side is FIT_ANALYSIS_RUN)
    FIT_VIEWED: 'fit_viewed',                              // {university_id, surface: page|modal}

    // Admin-issued free credits → email → claim. The funnel worth watching is
    // issued → opened → claimed; a low claim rate means the email or the offer
    // is wrong, not that the credits were unwanted.
    CREDIT_GRANT_ISSUED: 'credit_grant_issued',            // {recipients, credits_each, emailed, mode, from: credits|book_row} (from since #5186: the customer book's per-row Gift sends it too)
    CREDIT_GRANT_CLAIM_OPENED: 'credit_grant_claim_opened',// {signed_in}
    CREDIT_GRANT_CLAIMED: 'credit_grant_claimed',          // {credits}
    CREDIT_GRANT_CLAIM_FAILED: 'credit_grant_claim_failed',// {reason} — never the token

    // /welcome Act 3 — the payoff view and its follow-through (the two acts
    // before it already emit onboarding_grade_picked / onboarding_path_chosen)
    ONBOARDING_PICTURE_SHOWN: 'onboarding_picture_shown',
    ONBOARDING_PICTURE_CTA_CLICKED: 'onboarding_picture_cta_clicked', // {cta: mission|secondary|explore|first_school, band?}

    // The mobile first-run funnel (2026-07-30): picks → dive → guided
    // analysis → desktop pitch. Device split comes free from PostHog's
    // $device_type, so none of these carry an is_mobile prop.
    ONBOARDING_PICKS_SHOWN: 'onboarding_picks_shown',         // {reaches, targets, wait_ms}
    ONBOARDING_PICKS_TIMEOUT: 'onboarding_picks_timeout',     // {wait_ms, had_gpa}
    // The dive events also fire from the first-read bar (#2359) with
    // {surface: 'first_read_bar'}; /welcome's carry no surface.
    ONBOARDING_DIVE_CONFIRMED: 'onboarding_dive_confirmed',   // {band, surface?}
    ONBOARDING_DIVE_CANCELLED: 'onboarding_dive_cancelled',   // {surface?, reason?: left_page}
    ONBOARDING_DIVE_COMPLETED: 'onboarding_dive_completed',   // {duration_ms, landing: card}
    // The hand-off arrived (#4037): fired on the Launchpad when the first
    // read's card is found and ringed. target names where it put them.
    ONBOARDING_LANDED: 'onboarding_landed',                   // {university_id, target: card}
    ONBOARDING_CARD_GUIDE_DISMISSED: 'onboarding_card_guide_dismissed', // {university_id}
    ONBOARDING_DIVE_FAILED: 'onboarding_dive_failed',         // {surface?}
    // The first-read bar (#2359, founder pick C): the comeback for students
    // with a profile and no college. Funnel: shown -> tapped ->
    // onboarding_dive_confirmed {surface: first_read_bar} -> college_added.
    // Goal: share of profile-built students with a college within 7 days.
    // A dismiss rate above half of shown means it is noise: cut it back.
    FIRST_READ_PROMPT_SHOWN: 'first_read_prompt_shown',         // {surface: bar, page} once per page shown
    FIRST_READ_PROMPT_TAPPED: 'first_read_prompt_tapped',       // {surface, band}
    FIRST_READ_PROMPT_DISMISSED: 'first_read_prompt_dismissed', // {surface} "Not now": hidden 7 days
    FIT_GUIDE_STARTED: 'fit_guide_started',                   // {university_id}
    FIT_GUIDE_SKIPPED: 'fit_guide_skipped',                   // {step}
    FIT_GUIDE_FINALE_SHOWN: 'fit_guide_finale_shown',
    FIT_GUIDE_DONE: 'fit_guide_done',
    FIT_GUIDE_FEEDBACK_OPENED: 'fit_guide_feedback_opened',
    // "Your Year" grade plan (#1240): which move each grade actually clicks
    // is the read on whether the underclassman framing works.
    YEAR_PLAN_MOVE_CLICKED: 'year_plan_move_clicked',         // {move, grade, was_done}

    // RETIRED 2026-10-12 (#3980, founder pick E): FIT_SUMMARY_SHOWN
    // ('fit_summary_shown') and FIT_SUMMARY_EXPANDED ('fit_summary_expanded')
    // measured the phone Fit Card front door and its tap through to the five
    // tabs. Both views are gone; the Strategy page's events below replace them.
    // Do not reuse the names: they would read as a continuation of a funnel
    // that no longer exists.
    // The school's Strategy page (#3980): which folds students open is the
    // first read on what they want from a school beyond their standing.
    // folds_open is true where the page opens with every fold open (desktop),
    // so STRATEGY_SECTION_OPENED there counts re-opens, not reads.
    STRATEGY_PAGE_OPENED: 'strategy_page_opened',             // {university_id, sections, folds_open, guided}
    STRATEGY_SECTION_OPENED: 'strategy_section_opened',       // {university_id, section: essays|aid|plan|watchouts}
    STRATEGY_MOVE_ADDED: 'strategy_move_added',               // {university_id} the one move, onto the roadmap
    STRATEGY_LINK_CLICKED: 'strategy_link_clicked',           // {university_id, link: major_chances|all_majors|essay_help|launchpad_card}
    DESKTOP_NUDGE_SHOWN: 'desktop_nudge_shown',
    DESKTOP_NUDGE_DISMISSED: 'desktop_nudge_dismissed',
    // Sign-in resilience (ops): how often the popup-first path degrades.
    // {code, waited_s?}: Firebase's code for a popup that could not open, or
    // stratia/popup-unseen when the student chose "Sign in in this tab" on
    // the overlay (#3483), with the seconds they had waited.
    SIGNIN_POPUP_FALLBACK: 'signin_popup_fallback',
    SIGNIN_REDIRECT_STATE_LOST: 'signin_redirect_state_lost',
    // Any sign-in failure, blocked or not (2026-07-29 login complaint: the
    // started→completed gap was invisible). {code, cancelled: bool, reason,
    // flow, app?} — `reason` (signInTrouble.REASONS) is the Desk's sign-in
    // health dimension per device (#2457); a sign-in whose page died
    // mid-flight is counted on the next load as code stratia/sign-in-lost,
    // with waited_s (start to that load) and load (reload|back_forward|
    // navigate|prerender|unknown: how the page was left, #3483). A redirect
    // the student left by pressing Back on Google's page is code
    // stratia/sign-in-back, reason redirect_back, cancelled: true (#3894;
    // restored: true when the page came back from the back/forward cache).
    SIGNIN_FAILED: 'signin_failed',
    // The button on a sign-in failure notice (#2457): {reason, action:
    // retry|copy_link}. Failed → recovery clicked → completed is the loop.
    SIGNIN_RECOVERY_CLICKED: 'signin_recovery_clicked',
    // ===== #2997 complete-visibility pass (2026-10-03). Each names the funnel
    // question it answers; ids, enums and counts only, never a typed word.
    // Does the contact form actually reach us? (Its SMTP secret expires and
    // 500s silently: customer-operations.)
    CONTACT_FORM_SUBMITTED: 'contact_form_submitted',      // {outcome: sent|failed, subject}
    // The /got-in form every Short points to (#4726, ADR 0056): of the
    // visitors a caption sends, how many start, how far they get, and what
    // stops them. A stored entry is counted SERVER-side
    // (outcome_form_submitted, and outcome_report_deleted, in
    // profile_manager_v2/outcome_reports.py), so there is no client twin
    // here. Never a GPA, score, college, major, email or delete code.
    OUTCOME_FORM_VIEWED: 'outcome_form_viewed',            // {src} once per page open
    OUTCOME_FORM_STARTED: 'outcome_form_started',          // {} the first tap or keystroke
    OUTCOME_FORM_STEP: 'outcome_form_step',                // {step: 2|3} reached
    OUTCOME_FORM_UNDER_13: 'outcome_form_under_13',        // {} turned away; nothing was sent
    OUTCOME_FORM_SUBMIT_FAILED: 'outcome_form_submit_failed', // {error: invalid|under_13|rate_limited|unavailable|network|other}
    OUTCOME_FORM_DELETE_REQUESTED: 'outcome_form_delete_requested', // {outcome: deleted|not_found|invalid|rate_limited|unavailable|network|other}
    // The Master's abroad waitlist on the landing page (#4967): does an India
    // visitor who starts the form finish it, and what stops them? The view
    // and the stored sign-up are counted SERVER-side (masters_waitlist_viewed,
    // masters_waitlist_joined, profile_manager_v2/masters_waitlist.py), so
    // there is no client twin. Never the address or a typed word.
    MASTERS_WAITLIST_STARTED: 'masters_waitlist_started',          // {surface: landing|masters_page} the first tap or keystroke
    MASTERS_WAITLIST_SUBMIT_FAILED: 'masters_waitlist_submit_failed', // {error: email|programs|consent|invalid|rate_limited|unavailable|not_found|network|other, surface}
    // The aid comparison page had no event at all: is it used?
    AID_PACKAGE_SAVED: 'aid_package_saved',                // {editing: bool}
    AID_COMPARISON_VIEWED: 'aid_comparison_viewed',        // {packages} once per page open
    // What families like yours paid, per school (#5492, flag net_cost_estimate).
    // Counts and booleans only: never an income, an asset, a household size,
    // an index or a dollar range (PostHog is no place for family finances).
    COST_INTAKE_OPENED: 'cost_intake_opened',              // {source: page|row}
    COST_INTAKE_SAVED: 'cost_intake_saved',                // {optional_filled, checks_on, edited} after the server confirmed
    COST_ESTIMATE_VIEWED: 'cost_estimate_viewed',          // {schools, estimated, no_estimate} first answered read per open
    COST_ESTIMATE_DETAIL_OPENED: 'cost_estimate_detail_opened', // {university_id}
    COST_NPC_OPENED: 'cost_npc_opened',                    // {university_id} the school's own calculator
    COST_ESTIMATE_REPORTED: 'cost_estimate_reported',      // {university_id} "This looks wrong"
    // Data rights: how often families export or leave (churn signal). Fired
    // on the click, before the request; never anything about the content.
    DATA_EXPORT_REQUESTED: 'data_export_requested',        // {outcome: downloaded|failed}
    ACCOUNT_DELETION_REQUESTED: 'account_deletion_requested', // {}
    // Errors outside a React render (event handlers, promises, chunk loads).
    // Replay console capture is off (#2511), so before this they were
    // invisible. The error's name and the chunk it came from; never the
    // message, which can quote page content.
    JS_ERROR: 'js_error',                                  // {kind: error|rejection, error_kind, chunk, path, stale_build}
    // The Decision ledger and the Notebook had no "opened" denominator.
    DECISION_LEDGER_VIEWED: 'decision_ledger_viewed',      // {schools, decided}
    RESEARCH_NOTEBOOK_VIEWED: 'research_notebook_viewed',  // {notes}
    // Resources index -> which paper (the paper's own page view is $pageview).
    // from: 'page' is a product page's paper link (LearnMore, #5309), with
    // the page's path, since the signed-in sidebar no longer lists Resources.
    RESOURCE_OPENED: 'resource_opened',                    // {slug, from: index|page, page?}
    // The public Tour page's panel links and Major Doors' two exits.
    TOUR_LINK_CLICKED: 'tour_link_clicked',                // {panel, to}
    MAJOR_DOORS_CTA_CLICKED: 'major_doors_cta_clicked',    // {cta: try|pricing|source}
    // Astra Today (#3754, ADR 0041). The funnel: did the student who opened
    // Today see a move, and what did they do with it? The server records the
    // answer itself (astra_move_responded); these are the browser half: what
    // was on screen and which button was pressed.
    ASTRA_TODAY_VIEWED: 'astra_today_viewed',              // {moves, surface: today|astra}
    ASTRA_MOVE_SHOWN: 'astra_move_shown',                  // {rule, surface}
    ASTRA_MOVE_CLICKED: 'astra_move_clicked',              // {rule, action: do|later_open|later|not_for_me|reason, surface}
    ASTRA_TODAY_MORE_OPENED: 'astra_today_more_opened',    // {more}
    // Astra's weekly question (#3810, F). The funnel: shown -> answered
    // (chip | text) or skipped (not_now | stop) -> a proposed change
    // confirmed / edited / declined. Applied changes are counted server-side
    // (astra_question_applied). The paid update offered after a change:
    // ASTRA_QUESTION_REFRESH {step: start|done|failed|needs_plan}.
    ASTRA_QUESTION_SHOWN: 'astra_question_shown',          // {kind}
    ASTRA_QUESTION_ANSWERED: 'astra_question_answered',    // {kind, via}
    ASTRA_QUESTION_SKIPPED: 'astra_question_skipped',      // {kind, how}
    ASTRA_QUESTION_CHANGE_CONFIRMED: 'astra_question_change_confirmed',   // {kind}
    ASTRA_QUESTION_CHANGE_EDITED: 'astra_question_change_edited',         // {kind}
    ASTRA_QUESTION_CHANGE_DECLINED: 'astra_question_change_declined',     // {kind}
    ASTRA_QUESTION_REFRESH: 'astra_question_refresh',      // {step}
    // Astra Today web push (#3756). The funnel: asked -> yes -> browser said
    // granted -> a note opened or muted. Sends are counted server-side
    // (astra_push_sent / astra_push_failed).
    PUSH_OPTIN_SHOWN: 'push_optin_shown',                  // {kind: push|ios_install|ios_home}
    PUSH_OPTIN_ACCEPTED: 'push_optin_accepted',            // {kind}
    PUSH_OPTIN_DECLINED: 'push_optin_declined',            // {kind}
    PUSH_PERMISSION_RESULT: 'push_permission_result',      // {result: subscribed|denied|unsupported|error}
    PUSH_OPENED: 'push_opened',                            // {surface}
    PUSH_MUTED: 'push_muted',                              // {kind, via: push|link}
    // Notes from Astra (#3755): which switch a student changed in Settings.
    ASTRA_NOTES_CHANGED: 'astra_notes_changed',            // {setting}
};
